/**
 * Постраничные ассеты legacy: в CodeIgniter контроллеры складывали их в
 * $mainData['css'][] / $mainData['out_css'][] / $mainData['out_js'][], а main.tpl
 * вставлял <link>/<script> в <head> только на нужной странице (Main::docs — docs.min,
 * Main::cameras — cameras + плеер airCam, Main::contacts — API Яндекс.Карт).
 *
 * НИКАКИХ НОВЫХ СТИЛЕЙ: подключаются те же файлы из public/assets/css, что и в legacy.
 */

import { useEffect, useState } from 'react'

/** Абсолютный/протокол-относительный href оставляем как есть, короткое имя → /assets/css/{name}.css. */
function cssHref(name: string): string {
  return name.startsWith('//') || name.startsWith('http') || name.startsWith('/')
    ? name
    : `/assets/css/${name}.css`
}

/**
 * Подключает legacy-CSS на время жизни страницы (аналог $mainData['css'] / ['out_css']).
 * @param names имена файлов из /assets/css (без .css) либо готовые URL.
 */
export function useLegacyCss(...names: string[]): void {
  const key = names.join('|')

  useEffect(() => {
    const links: HTMLLinkElement[] = []

    for (const name of key.split('|')) {
      if (name === '') continue
      const href = cssHref(name)
      if (document.head.querySelector(`link[href="${href}"]`) !== null) continue

      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      document.head.appendChild(link)
      links.push(link)
    }

    return () => {
      for (const link of links) link.remove()
    }
  }, [key])
}

/** Один раз загруженные внешние скрипты (повторно не тянем — как при переходе по страницам legacy). */
const loadedScripts = new Set<string>()

const PLAYER_MAIN = 'https://cdn.airnet.ru/cctv/player/js/main.js'
const PLAYER_PROEYE = 'https://cdn.airnet.ru/cctv/player/js/proeye.js'
const PLAYER_CSS = 'https://cdn.airnet.ru/cctv/player/css/main.css'

/**
 * Плеер видеонаблюдения airnet (airCam).
 *
 * Хитрость: cdn/cctv/player/js/main.js подгружает СВОИ зависимости — класс плеера
 * proeye.js и css — только внутри обработчика DOMContentLoaded. В обычном сайте
 * (legacy) скрипт стоит в <head>, и событие приходит уже после него. В SPA страница
 * давно загружена, скрипт вставляется динамически, DOMContentLoaded не повторяется —
 * proeye.js не подгружался вовсе, и клик по камере падал с
 * «Uncaught ReferenceError: ProEye_Player is not defined».
 * Поэтому зависимости грузим сами, ровно те же, что грузит main.js.
 *
 * @returns true, когда и airCam, и класс ProEye_Player доступны — до этого клики блокируем.
 */
export function useCameraPlayer(): boolean {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false

    const ensureCss = (): void => {
      if (document.head.querySelector(`link[href^="${PLAYER_CSS}"]`) !== null) return
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = PLAYER_CSS
      document.head.appendChild(link)
    }

    const loadScript = (src: string): Promise<void> =>
      new Promise((resolve, reject) => {
        if (loadedScripts.has(src)) {
          resolve()
          return
        }
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`)
        if (existing !== null) {
          existing.addEventListener('load', () => resolve())
          existing.addEventListener('error', () => reject(new Error(src)))
          return
        }
        const script = document.createElement('script')
        script.src = src
        script.async = false // порядок важен: proeye.js ожидает уже загруженный main.js
        script.addEventListener('load', () => {
          loadedScripts.add(src)
          resolve()
        })
        script.addEventListener('error', () => reject(new Error(src)))
        document.head.appendChild(script)
      })

    void (async () => {
      try {
        ensureCss()
        await loadScript(PLAYER_MAIN)
        await loadScript(PLAYER_PROEYE)
        if (!cancelled) setReady(true)
      } catch {
        if (!cancelled) setReady(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  return ready
}

/**
 * Внешний скрипт из legacy $mainData['out_js'] (плеер airCam, API Яндекс.Карт).
 * @returns true, когда скрипт загружен и его глобал доступен.
 */
export function useExternalScript(src: string): boolean {
  const [ready, setReady] = useState(loadedScripts.has(src))

  useEffect(() => {
    if (loadedScripts.has(src)) {
      setReady(true)
      return
    }

    let cancelled = false
    const onLoad = (): void => {
      loadedScripts.add(src)
      if (!cancelled) setReady(true)
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`)
    const script = existing ?? document.createElement('script')
    script.addEventListener('load', onLoad)

    if (existing === null) {
      script.src = src
      script.async = true
      document.body.appendChild(script)
    }

    return () => {
      cancelled = true
      script.removeEventListener('load', onLoad)
    }
  }, [src])

  return ready
}
